/**
 * @module auth
 * @description Public session and identity adapter contract surface. Runtime
 * auth implementations live in provider packages; this subpath exports only the
 * interface, Zod schemas, and thin error classes.
 */

import type {
  AdapterHealth as SharedAdapterHealth,
  AuthAdapter as SharedAuthAdapter,
  AuthErrorCode as SharedAuthErrorCode,
  AuthIdentity as SharedAuthIdentity,
  AuthProviderAdapter as SharedAuthProviderAdapter,
  AuthSession as SharedAuthSession,
  AuthSessionContract as SharedAuthSessionContract,
  AuthUser as SharedAuthUser,
  OAuthProvider as SharedOAuthProvider,
} from "@geenius/adapters";
import {
  AdapterHealthSchema as SharedAdapterHealthSchema,
  AuthContractError as SharedAuthContractError,
  AuthError as SharedAuthError,
  AuthIdentitySchema as SharedAuthIdentitySchema,
  AuthSessionContractSchema as SharedAuthSessionContractSchema,
} from "@geenius/adapters";

/**
 * Health result returned by auth providers after checking identity/session
 * storage and provider reachability.
 *
 * @example
 * ```ts
 * const health: AdapterHealth = await authProvider.getHealth();
 * ```
 */
export type AdapterHealth = SharedAdapterHealth;

/**
 * User-facing authentication operations for credential, OAuth, session, and
 * profile flows exposed by downstream auth providers.
 *
 * @example
 * ```ts
 * const session = await auth.signIn("user@example.com", "secret");
 * ```
 */
export type AuthAdapter = SharedAuthAdapter;

/**
 * Stable machine-readable auth error codes emitted by `AuthError`.
 *
 * @example
 * ```ts
 * const code: AuthErrorCode = "INVALID_CREDENTIALS";
 * ```
 */
export type AuthErrorCode = SharedAuthErrorCode;

/**
 * Provider-neutral identity record used by SDK-free auth provider adapters.
 *
 * @example
 * ```ts
 * const identity: AuthIdentity = { id: "u1", roles: [], metadata: {} };
 * ```
 */
export type AuthIdentity = SharedAuthIdentity;

/**
 * Low-level provider contract for identity persistence, session lifecycle, and
 * auth provider health checks.
 *
 * @example
 * ```ts
 * const identity = await provider.getIdentity("u1");
 * ```
 */
export type AuthProviderAdapter = SharedAuthProviderAdapter;

/**
 * Active user session returned by user-facing auth adapters.
 *
 * @example
 * ```ts
 * const session: AuthSession = { userId: "u1", user: { id: "u1" } };
 * ```
 */
export type AuthSession = SharedAuthSession;

/**
 * Serializable provider session record used at the contract boundary.
 *
 * @example
 * ```ts
 * const session: AuthSessionContract = { id: "s1", identityId: "u1", token: "token", createdAt: new Date().toISOString() };
 * ```
 */
export type AuthSessionContract = SharedAuthSessionContract;

/**
 * Profile fields associated with the active authenticated user.
 *
 * @example
 * ```ts
 * const user: AuthUser = { id: "u1", email: "user@example.com" };
 * ```
 */
export type AuthUser = SharedAuthUser;

/**
 * OAuth provider identifier accepted by `AuthAdapter.signInWithOAuth()`.
 *
 * @example
 * ```ts
 * const provider: OAuthProvider = "github";
 * ```
 */
export type OAuthProvider = SharedOAuthProvider;

/**
 * Runtime validator for the common auth adapter health response.
 *
 * @example
 * ```ts
 * const health = AdapterHealthSchema.parse({ ok: true, checkedAt: new Date().toISOString() });
 * ```
 */
export const AdapterHealthSchema = SharedAdapterHealthSchema;

/**
 * Contract-layer error for invalid auth payloads or provider-boundary
 * violations before a concrete auth SDK receives the request.
 *
 * @example
 * ```ts
 * throw new AuthContractError("Invalid auth identity");
 * ```
 */
export const AuthContractError = SharedAuthContractError;

/**
 * Runtime auth error class that preserves the auth domain, adapter error code,
 * and HTTP-like status code for credential, OAuth, and session failures.
 *
 * @example
 * ```ts
 * throw new AuthError("Invalid credentials", "INVALID_CREDENTIALS");
 * ```
 */
export const AuthError = SharedAuthError;

/**
 * Runtime validator for provider-neutral auth identity records.
 *
 * @example
 * ```ts
 * const identity = AuthIdentitySchema.parse({ id: "u1" });
 * ```
 */
export const AuthIdentitySchema = SharedAuthIdentitySchema;

/**
 * Runtime validator for provider-neutral auth session records.
 *
 * @example
 * ```ts
 * const session = AuthSessionContractSchema.parse({ id: "s1", identityId: "u1", token: "token", createdAt: new Date().toISOString() });
 * ```
 */
export const AuthSessionContractSchema = SharedAuthSessionContractSchema;
